import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — CHART actors (Flat-Motion pack; REFERENCE-STUDY §4.7).
 *
 * Real animated data-viz for genuinely quantitative beats — a DONUT that sweeps
 * to a percentage with a counting center label, and BARS that grow to labeled
 * values. HONESTY RULE: charts only ever display numbers that came from the
 * beat itself (narration % or AI-provided label=value pairs) — we never invent
 * data. Non-numeric "trend" beats keep the abstract Grow visual instead.
 *
 * items contract (set by the Director or the AI):
 *   ["donut", "40", "of adults"]                → 40% donut, caption "of adults"
 *   ["bars", "Renting=1800", "Owning=2400", …]  → grown bars with value labels
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const easeOut = (x: number) => 1 - Math.pow(1 - clamp01(x), 3);

export type ChartSpec =
  | { type: "donut"; pct: number; label: string }
  | { type: "bars"; bars: { label: string; value: number }[] }
  | null;

/** Parse the items contract. Returns null when it isn't a valid chart. */
export function parseChartSpec(items: string[]): ChartSpec {
  if (items[0] === "donut") {
    const pct = Number(items[1]);
    if (Number.isFinite(pct) && pct > 0 && pct <= 100) {
      return { type: "donut", pct, label: items[2] ?? "" };
    }
    return null;
  }
  if (items[0] === "bars") {
    const bars = items.slice(1)
      .map((s) => {
        const m = s.match(/^(.*?)[=:]\s*([\d,.]+)\s*$/);
        return m ? { label: m[1].trim(), value: Number(m[2].replace(/,/g, "")) } : null;
      })
      .filter((b): b is { label: string; value: number } => !!b && Number.isFinite(b.value) && b.value >= 0);
    if (bars.length >= 2) return { type: "bars", bars: bars.slice(0, 5) };
    return null;
  }
  return null;
}

/** Pull an honest percentage out of narration text ("about 40 percent", "40%"). */
export function extractPercent(text: string): number | null {
  const m = text.match(/\b(\d{1,3}(?:\.\d+)?)\s*(?:%|percent\b)/i);
  if (!m) return null;
  const pct = Number(m[1]);
  return Number.isFinite(pct) && pct > 0 && pct <= 100 ? pct : null;
}

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

const Donut: React.FC<VProps & { u: number; pct: number; label: string }> = ({ box, theme, t, u, pct, label }) => {
  const P = theme.palette;
  const cx = box.cx, cy = box.cy - 2 * u;
  const R = Math.min(box.w, box.h) * 0.26;
  const thick = u * 2.6;
  const sweep = easeOut(t / 1.8) * (pct / 100);
  const shown = Math.round(easeOut(t / 1.8) * pct);
  // arc path for the swept fraction (from 12 o'clock, clockwise)
  const a0 = -Math.PI / 2;
  const a1 = a0 + sweep * TAU;
  const large = sweep > 0.5 ? 1 : 0;
  const p0 = { x: cx + Math.cos(a0) * R, y: cy + Math.sin(a0) * R };
  const p1 = { x: cx + Math.cos(a1) * R, y: cy + Math.sin(a1) * R };
  // a highlight dot keeps orbiting the rim after the sweep settles
  const orbA = a0 + ((t * 0.35) % 1) * TAU;
  return (
    <g>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke={theme.dark ? "#2a3038" : "#e4e8ee"} strokeWidth={thick} />
      {sweep > 0.005 && (
        <path d={`M ${p0.x} ${p0.y} A ${R} ${R} 0 ${large} 1 ${p1.x} ${p1.y}`}
          fill="none" stroke={P.primary} strokeWidth={thick} strokeLinecap="round" />
      )}
      <circle cx={cx + Math.cos(orbA) * R} cy={cy + Math.sin(orbA) * R} r={u * 0.55} fill={P.highlight} opacity={0.9} />
      <text x={cx} y={cy + u * 1.2} textAnchor="middle" fontFamily={theme.body.family}
        fontWeight={800} fontSize={u * 5} fill={P.ink}>{shown}%</text>
      {label && (
        <text x={cx} y={cy + R + u * 4} textAnchor="middle" fontFamily={theme.body.family}
          fontWeight={700} fontSize={u * 1.9} fill={P.sub}>{label}</text>
      )}
    </g>
  );
};

const Bars: React.FC<VProps & { u: number; bars: { label: string; value: number }[] }> = ({ box, theme, t, u, bars }) => {
  const P = theme.palette;
  const max = Math.max(...bars.map((b) => b.value), 1);
  const baseY = box.y + box.h * 0.78;
  const top = box.y + box.h * 0.16;
  const left = box.x + box.w * 0.14, right = box.x + box.w * 0.86;
  const n = bars.length;
  const bw = ((right - left) / n) * 0.52;
  const lead = bars.reduce((im, b, i) => (b.value > bars[im].value ? i : im), 0);
  return (
    <g>
      <line x1={left - u} y1={baseY} x2={right + u} y2={baseY} stroke={P.muted} strokeWidth={2} strokeOpacity={0.4} />
      {bars.map((b, i) => {
        const x = lerpX(left, right, n, i);
        const grow = easeOut((t - i * 0.35) / 1.3);
        const h = (baseY - top) * (b.value / max) * grow;
        const isLead = i === lead;
        // settled bars keep breathing very slightly; the leader pulses its cap
        const breathe = 1 + 0.015 * Math.sin(t * 2 + i);
        const col = isLead ? P.primary : P.secondary;
        return (
          <g key={i}>
            <rect x={x - bw / 2} y={baseY - h * breathe} width={bw} height={Math.max(0, h * breathe)}
              rx={u * 0.5} fill={col} opacity={0.92} />
            {grow > 0.95 && (
              <text x={x} y={baseY - h - u * 1.1} textAnchor="middle" fontFamily={theme.body.family}
                fontWeight={800} fontSize={u * 2.1} fill={P.ink}>{fmt(b.value)}</text>
            )}
            <text x={x} y={baseY + u * 2.4} textAnchor="middle" fontFamily={theme.body.family}
              fontWeight={700} fontSize={u * 1.8} fill={P.sub}>{trunc(b.label, 10)}</text>
          </g>
        );
      })}
    </g>
  );
};

const lerpX = (l: number, r: number, n: number, i: number) => l + ((r - l) * (i + 0.5)) / n;
const fmt = (v: number) => (v >= 1000 ? v.toLocaleString("en-US") : String(v));
const trunc = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

export const ChartActor: React.FC<VProps> = (props) => {
  const spec = parseChartSpec(props.items);
  const u = Math.min(props.box.h / 36, props.box.w / 34);
  if (!spec) return null; // caller guards; never render a fake chart
  return spec.type === "donut"
    ? <Donut {...props} u={u} pct={spec.pct} label={spec.label} />
    : <Bars {...props} u={u} bars={spec.bars} />;
};
